import {CustomerRepresentation} from './customer-representation';
import {Test} from '../../../test/state/test';
import {TestRepresentation} from '../../../test/state/test-representation';

export enum myCustomersAction {
  NEW_TEST_INVITE = 'New_test_invite',
  NEW_INVITE = 'New_invite',
  DONE = 'Done',
  GRANT = 'Grant',
  IN_PROGRESS = 'In_progress',
  CHANGE_PRODUCT = 'Change_product',
  DELETING = 'Deleting'
}

export class Customer {

  private _keycloakId: string;
  private _firstName: string;
  private _lastName: string;
  private _email: string;
  private _groupName: string;
  private _tests: Test[];
  private _creationDate: Date;
  private _lastInviteMailSent: Date;
  private readonly _emailVerified: boolean;

  constructor(keycloakId: string,
              firstName: string,
              lastName: string,
              email: string,
              groupName: string,
              tests: Test[],
              creationDate: Date,
              lastInviteMailSent: Date,
              emailVerified: boolean) {
    this._keycloakId = keycloakId;
    this._firstName = firstName;
    this._lastName = lastName;
    this._email = email || '';
    this._groupName = groupName;
    this._tests = tests;
    this._creationDate = creationDate;
    this._lastInviteMailSent = lastInviteMailSent;
    this._emailVerified = emailVerified;
  }

  get keycloakId(): string {
    return this._keycloakId;
  }

  set keycloakId(value: string) {
    this._keycloakId = value;
  }

  get firstName(): string {
    return this._firstName.trim();
  }

  set firstName(value: string) {
    this._firstName = value;
  }

  get lastName(): string {
    return this._lastName.trim();
  }

  set lastName(value: string) {
    this._lastName = value;
  }

  get email(): string {
    return this._email;
  }

  set email(value: string) {
    this._email = value;
  }

  get groupName(): string {
    return this._groupName;
  }

  set groupName(value: string) {
    this._groupName = value;
  }

  get tests(): Test[] {
    return this._tests;
  }

  set tests(value: Test[]) {
    this._tests = value;
  }

  get creationDate(): Date {
    return this._creationDate;
  }

  set creationDate(value: Date) {
    this._creationDate = value;
  }

  get fullName(): string {
    return this.firstName + ' ' + this.lastName;
  }

  get emailVerified(): boolean {
    return this._emailVerified;
  }

  get lastInviteMailSent(): Date {
    return this._lastInviteMailSent;
  }

  set lastInviteMailSent(value: Date) {
    this._lastInviteMailSent = value;
  }

  static buildFrom(customerRepresentation: CustomerRepresentation): Customer {
    console.log(customerRepresentation);
    return new Customer(
      customerRepresentation.keycloakId,
      customerRepresentation.firstName,
      customerRepresentation.lastName,
      customerRepresentation.email,
      customerRepresentation.groupName,
      customerRepresentation.tests.map(testRepresentation => TestRepresentation.toModelEntity(testRepresentation)),
      new Date(customerRepresentation.creationDate),
      new Date(customerRepresentation.lastInviteMailSent),
      Boolean(customerRepresentation.emailVerified)
    );
  }

  public hasTests(): boolean {
    return this._tests.length > 0;
  }

  public hasNoTests(): boolean {
    return !this.hasTests();
  }

  public getFirstTest(): Test | undefined {
    return this.hasTests() ? this.tests[0] : undefined;
  }

  public hasStartedTests(): boolean {
    return this._tests.some((test: Test) => test.started);
  }

  public hasFinishedTests(): boolean {
    return this._tests.some((test: Test) => test.finished);
  }

  public getIdOfFirstFinishedTest(): string | undefined {
    return this._tests.filter(test => test.finished).map(test => test.testId).shift();
  }

  public needsNewInviteMail(): boolean {
    return !this.emailVerified && this.unverifiedEmailForOneMonth() && this.lastEmailSentOlderThan30Days();
  }

  public getMyCustomersAction(isDeleting: boolean = false): myCustomersAction {
    if (isDeleting) {
      return myCustomersAction.DELETING;
    }
    if (this.hasNoTests()) {
      return myCustomersAction.NEW_TEST_INVITE;
    }
    if (this.needsNewInviteMail()) {
      return myCustomersAction.NEW_INVITE;
    }
    if (this.tests[0].accessGranted) {
      return myCustomersAction.DONE;
    }
    if (this.tests[0].finished) {
      return myCustomersAction.GRANT;
    }
    if (this.tests[0].started) {
      return myCustomersAction.IN_PROGRESS;
    }
    return myCustomersAction.CHANGE_PRODUCT;
  }

  private unverifiedEmailForOneMonth(): boolean {
    const diff = Date.now().valueOf() - this.creationDate.valueOf();
    return Math.ceil(diff / (1000 * 3600 * 24)) > 29;
  }

  private lastEmailSentOlderThan30Days(): boolean {
    const diff = Date.now().valueOf() - this.lastInviteMailSent.valueOf();
    // console.log(this.lastInviteMailSent.valueOf());
    return Math.ceil(diff / (1000 * 3600 * 24)) > 29;
  }

  public getVariationOfFirstTest(): string | undefined {
    return this._tests.map(test => {
      if (test.variationUpdateAllowed && !test.started) {
        return 'test.variation.unspecified';
      }
      return test.variation.translatorKey;
    }).shift();
  }

}
